<template>
  <div>
    <el-autocomplete
      ref="search"
      v-model="selected"
      clearable
      :debounce="200"
      :validate-event="false"
      :fetch-suggestions="search"
      highlight-first-item
      placeholder="Search OpenFreight"
      placement="bottom-end"
      :trigger-on-focus="false"
      @select="handleSelect"
    >
      <!--   es-lint ignore-next-line   -->
      <template
        v-if="filteredResource"
        slot="prepend"
      >
        {{ filteredResource ? `${filteredResource} ` : '' }}
      </template>
      <!--   es-lint ignore-next-line   -->
      <i
        slot="suffix"
        class="el-input__icon el-icon-question"
        @click="showHelp"
      />
      <template v-slot="{ item }">
        <template v-if="item.constructor.name === 'account'">
          {{ $_.get(item, 'name') || '-' }}&nbsp;<el-tag
            :type="getStatusTagType($_.get(item, 'status', '-'))"
            size="mini"
          >
            {{ $filters.titleize($_.get(item, 'status') || 'none') }}
          </el-tag>&nbsp;<span
            class="text-muted"
            style="float: right"
          >Account</span>
        </template>

        <template v-else-if="item.constructor.name === 'carrier'">
          {{ $_.get(item, 'name') || '-' }}&nbsp;<span
            class="text-muted"
            style="float: right"
          >Carrier</span>
        </template>

        <template v-else-if="item.constructor.name === 'pickup'">
          {{ $_.get(item, 'booking_reference') || '-' }}&nbsp;<small class="text-muted">Created: {{ $moment($_.get(item, 'created_at')).fromNow() }}. Status: {{ $filters.titleize($_.get(item, 'status')) }} </small>&nbsp;<span
            class="text-muted"
            style="float: right"
          >Pickup</span>
        </template>

        <template v-else-if="item.constructor.name === 'profile'">
          {{ $_.get(item, 'name_full') || '-' }}, {{ $_.get(item, 'email') || '-' }}&nbsp;<span
            class="text-muted"
            style="float: right"
          >Profile</span>
        </template>

        <template v-else-if="item.constructor.name === 'account_team'">
          {{ $_.get(item, 'name') || '-' }} [{{ $_.get(item, 'description') || '-' }}]&nbsp;<span
            class="text-muted"
            style="float: right"
          >Team</span>
        </template>

        <template v-else-if="item.constructor.name === 'consignment'">
          {{ $_.get(item, 'con_full') || '-' }} &nbsp;<el-tag
            type="primary"
            size="mini"
          >
            {{ $_.get(item, 'account.name') }}
          </el-tag>&nbsp;<el-tag
            type="info"
            size="mini"
          >
            {{ $_.get(item, 'carrier.name') }}
          </el-tag>&nbsp;<span
            class="text-muted"
            style="float: right"
          >Consignment</span>
        </template>
      </template>
    </el-autocomplete>
    <el-dialog
      v-if="peek"
      :title="dialogTitle"
      visible
      width="70vw"
      :close-on-click-modal="false"
      @close="clearPeek"
    >
      <div
        v-if="isLoading"
        v-loading="isLoading"
      />
      <component
        :is="peek"
        v-else
        v-bind="peekProps"
        style="font-size: 12px;"
      />
      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button
          size="small"
          @click.native="clearPeek"
        >
          Cancel
        </el-button>
        <el-button
          v-if="routeName"
          size="small"
          type="primary"
          @click.native="redirect"
        >
          Details
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import AccountPeek from '@/components/Application/Manage/Accounts/Peek'
import PickupPeek from '@/components/Application/CustomerService/Pickups/Pickup/Peek'
import ProfilePeek from '@/components/Application/Manage/Users/Detail'
import CarrierPeek from '@/components/Application/Manage/Carriers/Detail'
import ConsignmentPeek from '@/components/Application/Freight/ConsignmentPeek'
import { mapGetters } from 'vuex'

export default {
  components: {
    AccountPeek,
    PickupPeek,
    ProfilePeek,
    CarrierPeek,
    ConsignmentPeek
  },
  data () {
    return {
      searchAbortController: undefined,
      selected: null,
      dialogTitle: '',
      peek: null,
      peekProps: null,
      isLoading: true,
      routeName: '',
      routeParams: null,
      filteredResource: null
    }
  },
  computed: {
    ...mapGetters({
      searchable: 'configuration/search/searchable',
      isShowPeek: 'configuration/search/isShowPeek',
      isNewSearch: 'configuration/search/isNewSearch',
      resultsPerPage: 'configuration/search/resultsPerPage'
    })
  },
  watch: {
    selected (newValue) {
      const resourceFilter = newValue && newValue[1] === '/' ? newValue[0] : null
      const resourceMap = {
        a: 'Accounts',
        s: 'Carriers',
        p: 'Pickups',
        u: 'Profiles',
        t: 'Teams',
        c: 'Consignments'
      }
      this.filteredResource = resourceFilter ? resourceMap[resourceFilter] : null
    }
  },
  mounted () {
    window.addEventListener('keydown', this.escapeListener)
  },
  methods: {
    showHelp () {
      const info = `
        <p>You can search for the following resources:</p>
        <ul>
          <li>Accounts (a)</li>
          <li>Carriers (s)</li>
          <li>Pickups (p)</li>
          <li>Profiles (u)</li>
          <li>Teams (t)</li>
          <li>Consignments (c)</li>
        </ul>
        <p>To search for only a single resource type, enter the shortcut letter and either a front or backslash. Eg. To only show accounts matching TIG you would enter <i>a/TIG</i></p>
        <br>
        <p>To quickly focus on the search bar, press the backtick key (<code>\`</code>).</p>
      `
      this.$confirm(info, 'Search', {
        confirmButtonText: 'OK',
        dangerouslyUseHTMLString: true
      }).then(() => {
        //
      }).catch(() => {
        //
      })
    },
    escapeListener (event) {
      if (event.key === '`') {
        event.preventDefault()
        this.$refs['search'].focus()
      }
    },
    async search (query, callback) {
      if (typeof this.searchAbortController !== 'undefined') {
        this.searchAbortController.abort()
      }

      this.searchAbortController = new AbortController()

      try {
        const response = await this
          .$sdk
          .search(
            query,
            this.searchable.join(','),
            undefined,
            this.searchAbortController.signal,
            this.isNewSearch,
            this.resultsPerPage
          )

        callback(response)
      } catch {
        //
      } finally {
        this.searchAbortController = undefined
      }
    },
    handleSelect (selection) {
      if (selection.constructor.name === 'account') {
        this.routeName = 'customerservice-accounts-account'
        this.routeParams = { accountId: selection.id }

        if (this.isShowPeek) {
          this.isLoading = true
          this.dialogTitle = `Account Details - ${this.$_.get(selection, 'name', '-')}`
          this.peek = AccountPeek
          this.$sdk.account.read(selection.id, builder => {
            builder.include = 'portfolio.profile,b_group,parent'
          }).then(res => { this.peekProps = { account: res } })
            .finally(() => { this.isLoading = false })
        } else {
          this.$router.push({
            name: this.routeName,
            params: this.routeParams
          })
        }
      }

      if (selection.constructor.name === 'carrier') {
        this.routeName = 'manage-carriers-carrier'
        this.routeParams = { carrierId: selection.id }

        if (this.isShowPeek) {
          this.isLoading = true
          this.dialogTitle = `Carrier Details - ${this.$_.get(selection, 'name', '-')}`
          this.peek = CarrierPeek
          this.$sdk.carrier.read(selection.id)
            .then(res => { this.peekProps = { carrier: res } })
            .finally(() => { this.isLoading = false })
        } else {
          this.$router.push({
            name: this.routeName,
            params: this.routeParams
          })
        }
      }

      if (selection.constructor.name === 'pickup') {
        this.routeName = 'customerservice-pickups-pickup'
        this.routeParams = { pickupId: selection.id }

        if (this.isShowPeek) {
          this.isLoading = true
          this.dialogTitle = `Pickup Details - ${this.$_.get(selection, 'booking_reference', '-')}`
          this.peek = PickupPeek
          this.$sdk.pickup.read(selection.id, builder => {
            builder.include = [
              'account.carrier_settings.carrier',
              'profile',
              'carrier',
              'carrier_service',
              'sender',
              'receiver',
              'consignments.items.type',
              'items.type',
              'customer_time_zone',
              'team.shipper_setting.address',
              'events.profile'
            ]
          }).then(res => { this.peekProps = { pickup: res } })
            .finally(() => { this.isLoading = false })
        } else {
          this.$router.push({
            name: this.routeName,
            params: this.routeParams
          })
        }
      }

      if (selection.constructor.name === 'profile') {
        this.isLoading = true
        this.dialogTitle = `Profile Details - ${this.$_.get(selection, 'name_full', '-')}`
        this.peek = ProfilePeek
        this.$sdk.profile.read(selection.id, builder => {
          builder.include = 'teams,teams.account'
        })
          .then(res => { this.peekProps = { profile: res } })
          .finally(() => { this.isLoading = false })

        // this.routeName = 'manage-users-user'
        // this.routeParams = { userId: selection.id }
      }

      if (selection.constructor.name === 'consignment') {
        this.routeName = 'freight-consignments-v3-consignment'
        this.routeParams = { consignmentId: selection.id }

        if (this.isShowPeek) {
          this.isLoading = true
          this.dialogTitle = `Consignment Details - ${this.$_.get(selection, 'con_full', '-')}`
          this.peek = ConsignmentPeek
          this.$sdk.consignment.read(selection.id, builder => {
            builder.include = [
              'teams.account',
              'trackings',
              'service',
              'carrier',
              'items',
              'items.type'
            ]
          })
            .then(res => { this.peekProps = { consignment: res } })
            .finally(() => { this.isLoading = false })
        } else {
          this.$router.push({
            name: this.routeName,
            params: this.routeParams
          })
        }
      }

      if (selection.constructor.name === 'account_team' || selection.constructor.name === 'account.team' || selection.constructor.name === 'team') {
        this.$sdk.team.read(selection.id, builder => {
          builder.include = [ 'account' ]
        }).then(res => {
          this.routeParams = { accountId: res.account.id, teamId: selection.id }
        }).finally(() => {
          this.isLoading = false
          this.routeName = 'manage-accounts-account-configurations-teams-team'
          this.$router.push({
            name: this.routeName,
            params: this.routeParams
          })
        })
      }
    },
    redirect () {
      this.$router.push({
        name: this.routeName,
        params: this.routeParams
      })
      this.clearPeek()
    },
    clearPeek () {
      this.peek = null
      this.routeName = ''
      this.routeParams = null
    },
    getStatusTagType (status) {
      switch (status) {
        case 'lead':
          return ''
        case 'pending':
          return 'info'
        case 'trading':
          return 'success'
        case 'on hold':
          return 'warning'
        case 'closed':
          return 'danger'
        default:
          return 'info'
      }
    },
    clearCarrierInput () {
      this.$set(this, 'selected', '')
    }
  }
}
</script>

<style lang="scss" scoped>
  .el-autocomplete {
    width: 100%;

    ::v-deep .el-input__inner {
      color: #fff;
      background-color: #3984d2;
      border: none;
      transition: background-color 230ms linear;

      &::placeholder {
        color: #fff;
      }

      &:focus {
        background-color: #2b73be;
      }
    }
  }
</style>
